import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from 'shared/lib/cn';
import type { SectionStatus } from 'shared/state/sectionStatusContext';

export type NavItemProps = {
  label: string; icon: LucideIcon; active?: boolean; disabled?: boolean; title?: string; onSelect: () => void;
  dot?: SectionStatus;
  /** Keyboard shortcut shown at the end of the row, e.g. "g 3". Display only. */
  hint?: string;
  /** Set (true or false) only on a toggle: publishes aria-pressed. */
  pressed?: boolean;
};

export function NavItem({ label, icon: Icon, active = false, disabled = false, title, onSelect, dot, hint, pressed }: NavItemProps) {
  // The dot is colour-only (bg-ok vs bg-brand) and would otherwise convey
  // section status to sighted users alone (WCAG 1.4.1 / 1.1.1). aria-label
  // OVERRIDES this button's contents for assistive tech, so an sr-only span
  // inside it would never be read — the accessible NAME itself has to carry
  // the status. 'ok' stays unannounced: a fully-configured section is the
  // expected, no-action state, and narrating "complete" on every satisfied
  // NavItem would be noise. 'warn' is the state a rep must act on, so it is
  // the one that was actually inaccessible before this change.
  const accessibleLabel = dot === 'warn' ? `${label}, needs attention` : label;

  return (
    <button
      type="button"
      aria-label={accessibleLabel}
      aria-current={active ? 'true' : undefined}
      aria-pressed={pressed}
      title={title ?? label}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        // overflow-hidden is what hides the label: at the sidebar's collapsed width
        // this button is only as wide as its icon cell, and the label, status and
        // hint that follow are clipped until the sidebar widens (Workbench).
        'flex h-11 w-full shrink-0 items-center overflow-hidden rounded-[var(--radius-2)] text-left text-ink-muted transition-colors hover:bg-surface-high hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/60 disabled:opacity-30 disabled:hover:bg-transparent max-[620px]:w-11',
        active && 'bg-brand/15 text-brand-strong',
      )}
    >
      {/* A fixed cell the width of the collapsed sidebar's column, so the icon does
          not move when the sidebar widens. The dot is anchored here, not to the
          button, for the same reason. */}
      <span className="relative flex h-11 w-12 shrink-0 items-center justify-center max-[620px]:w-11">
        <Icon className="h-5 w-5" />
        {dot && dot !== 'none' && (
          <span
            data-testid="nav-dot"
            data-status={dot}
            className={cn('absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full', dot === 'ok' ? 'bg-ok' : 'bg-brand')}
          />
        )}
      </span>
      {/* The bottom bar (max-[620px]) has no room for labels and no hover to reveal them. */}
      <span className="flex min-w-0 flex-1 flex-col justify-center pr-2 leading-tight max-[620px]:hidden">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-body text-sm font-medium">{label}</span>
        {dot === 'warn' && <span className="whitespace-nowrap font-body text-tiny text-brand-strong">Needs attention</span>}
      </span>
      {hint && (
        <kbd className="mr-2 shrink-0 whitespace-nowrap rounded-[var(--radius-2)] border border-hairline/20 px-1.5 font-body text-tiny text-ink-muted max-[620px]:hidden">
          {hint}
        </kbd>
      )}
    </button>
  );
}
